import { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  MapPin, Phone, Star, Clock, Navigation as NavIcon, Heart,
  Building2, Pill, Stethoscope, FlaskConical, Loader2, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const NearbyClinics = ({ user, setUser }) => {
  const [facilities, setFacilities] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [emergencyNumbers, setEmergencyNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedFacility, setSelectedFacility] = useState(null);

  useEffect(() => {
    getLocation();
    loadEmergencyNumbers();
  }, []);

  useEffect(() => {
    if (location) {
      loadFacilities();
    }
  }, [location, activeTab]);

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        () => {
          // Default location (Delhi)
          setLocation({ latitude: 28.6139, longitude: 77.2090 });
          toast.info('Using default location. Enable GPS for accurate results.');
        }
      );
    } else {
      setLocation({ latitude: 28.6139, longitude: 77.2090 });
    }
  };

  const loadFacilities = async () => {
    if (!location) return;
    
    setLoading(true);
    try {
      let endpoint = `${API}/nearby/facilities?latitude=${location.latitude}&longitude=${location.longitude}`;
      
      if (activeTab !== 'all' && activeTab !== 'favorites') {
        endpoint += `&type=${activeTab}`;
      }
      
      if (activeTab === 'emergency') {
        endpoint = `${API}/nearby/emergency?latitude=${location.latitude}&longitude=${location.longitude}`;
      }
      
      const [facilitiesRes, favoritesRes] = await Promise.all([
        axios.get(endpoint),
        axios.get(`${API}/nearby/favorites`)
      ]);
      
      setFacilities(facilitiesRes.data.facilities || []);
      setFavorites(favoritesRes.data.favorites || []);
    } catch (error) {
      console.error('Error loading facilities:', error);
      toast.error('Failed to load nearby facilities');
    } finally {
      setLoading(false);
    }
  };

  const loadEmergencyNumbers = async () => {
    try {
      const response = await axios.get(`${API}/nearby/emergency-numbers`);
      setEmergencyNumbers(response.data.numbers || []);
    } catch (error) {
      console.error('Error loading emergency numbers:', error);
    }
  };

  const saveFavorite = async (facilityId) => {
    try {
      await axios.post(`${API}/nearby/save-favorite/${facilityId}`);
      toast.success('Added to favorites');
      loadFacilities();
    } catch (error) {
      toast.error('Failed to save favorite');
    }
  };

  const getDirections = (facility) => {
    window.open(facility.directions_url, '_blank');
  };

  const callFacility = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const getTypeIcon = (type) => {
    const icons = {
      hospital: Building2,
      clinic: Stethoscope,
      pharmacy: Pill,
      diagnostic: FlaskConical,
      urgent_care: Stethoscope
    };
    const Icon = icons[type] || Building2;
    return <Icon className="w-5 h-5" />;
  };

  const getTypeColor = (type) => {
    const colors = {
      hospital: 'bg-red-100 text-red-700',
      clinic: 'bg-blue-100 text-blue-700',
      pharmacy: 'bg-green-100 text-green-700',
      diagnostic: 'bg-purple-100 text-purple-700',
      urgent_care: 'bg-orange-100 text-orange-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const displayFacilities = activeTab === 'favorites' ? favorites : facilities;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 pt-24 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <MapPin className="w-8 h-8 text-blue-600" />
            Nearby Clinics
          </h1>
          <p className="text-gray-600 mt-1">Find hospitals, clinics, and pharmacies near you</p>
          {location && (
            <p className="text-sm text-gray-500 flex items-center gap-1 mt-2">
              <MapPin className="w-4 h-4" />
              Location: {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
            </p>
          )}
        </div>

        {/* Quick Emergency Numbers */}
        <Card className="mb-6 bg-gradient-to-r from-red-500 to-orange-500 text-white">
          <CardContent className="py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                <span className="font-medium">Emergency:</span>
              </div>
              <div className="flex gap-3 flex-wrap">
                {emergencyNumbers.slice(0, 4).map((num, i) => (
                  <button
                    key={`item-${i}`}
                    onClick={() => callFacility(num.number)}
                    className="px-3 py-1 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                  >
                    {num.name}: {num.number}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-6 w-full">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="hospital">Hospitals</TabsTrigger>
            <TabsTrigger value="clinic">Clinics</TabsTrigger>
            <TabsTrigger value="pharmacy">Pharmacy</TabsTrigger>
            <TabsTrigger value="emergency">Emergency</TabsTrigger>
            <TabsTrigger value="favorites">Saved</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
              </div>
            ) : displayFacilities.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">No facilities found</p>
                  <p className="text-sm text-gray-400">Try expanding your search radius</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {displayFacilities.map((facility) => (
                  <Card key={facility.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Image */}
                        <div className="hidden md:block w-32 h-32 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={facility.image_url}
                            alt={facility.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400';
                            }}
                          />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-semibold text-lg text-gray-800">{facility.name}</h3>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className={getTypeColor(facility.type)}>
                                  {getTypeIcon(facility.type)}
                                  <span className="ml-1 capitalize">{facility.type.replace('_', ' ')}</span>
                                </Badge>
                                {facility.open_24h && (
                                  <Badge className="bg-green-100 text-green-700">24/7</Badge>
                                )}
                                {facility.emergency && (
                                  <Badge className="bg-red-100 text-red-700">Emergency</Badge>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-blue-600">{facility.distance_text}</p>
                              <div className="flex items-center gap-1 text-yellow-500">
                                <Star className="w-4 h-4 fill-current" />
                                <span className="text-gray-700">{facility.rating}</span>
                                <span className="text-gray-400 text-sm">({facility.reviews_count})</span>
                              </div>
                            </div>
                          </div>

                          <p className="text-gray-600 text-sm mb-2 flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {facility.address}
                          </p>

                          {facility.hours && (
                            <p className="text-gray-500 text-sm flex items-center gap-1 mb-2">
                              <Clock className="w-4 h-4" />
                              {facility.hours}
                            </p>
                          )}

                          {facility.specialties && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {facility.specialties.slice(0, 4).map((spec, i) => (
                                <Badge key={`item-${i}`} variant="outline" className="text-xs">{spec}</Badge>
                              ))}
                              {facility.specialties.length > 4 && (
                                <Badge variant="outline" className="text-xs">+{facility.specialties.length - 4}</Badge>
                              )}
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              onClick={() => getDirections(facility)}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <NavIcon className="w-4 h-4 mr-1" />
                              Directions
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => callFacility(facility.phone)}
                            >
                              <Phone className="w-4 h-4 mr-1" />
                              Call
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => saveFavorite(facility.id)}
                            >
                              <Heart className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default NearbyClinics;
